import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ReactComponent as CommentIcon } from '@assets/icons/order/Comment-not-read.svg';
import { ReactComponent as SlideIcon } from '@assets/icons/order/slide-icon.svg';

import LoaderComponent from '@components/LoaderComponent';
import CommonImage from '@components/CommonImage';

import AllTask from '@features/order-dashboard/component/AllTask';
import OrderSummary from '@features/order-dashboard/component/OrderSummary';
import OrderTask from '@features/task/components/OrderTask';
import {
	BrandAndTeamCell,
	BuyerEtdCell,
	ExFactoryCell,
	FabricCell,
	FactoryEtdCell,
	PoCell,
	QuotationBuyerPriceCell,
	QuotationFactoryPriceCell,
	ScCell,
	ShipmentCell,
	StyleCell,
	TnaStatusCell
} from '@features/order-dashboard/component/OrderDashboardTableCells';
import OrderRemarks from '@features/order-dashboard/component/OrderRemarks';

import { setCurrentActivePage } from '@store/actions/ActivePageAction';
import { storeNewMsg } from '@store/actions/ChatAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchEnumValues } from '@services/commonService';
import tnaService from '@services/tna-service';
import { completeOrderStepByIdList } from '@services/orderDashboard';

import { formatNumberWithSuffix } from '@utils/helper';
import {
	_getKey,
	capitalizeFirstLetter,
	changeDateFormat,
	getDateDifference,
	getShortName,
	printApiError,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

import { OrderContext } from '@context/OrderContext';

const filterBy = ['Order placed', 'Inspection', 'Delivery'];

const OrderDashboard = ({
	orderStore,
	callback,
	showSummary,
	requestFrom = 'ORDER_DASHBOARD',
	handleOrderDashboardScroll,
	isShowTasks = true,
	isShowRemarks = true,
	queryParams,
	updateOrderStepByOrders,
	getOrderInfoByDashboardId
}) => {
	const [loader] = useState(false);
	const [, setColors] = useState({});
	const [orderId, setOrderId] = useState(null);
	const [brandName, setBrandName] = useState('');
	const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
	const [showAllTask, setShowAllTask] = useState(false);
	const [reviseInfo, setReviseInfo] = useState(null);
	const [selectedTaskId, setSelectedTaskId] = useState(0);
	const [selectedItem, setSelectedItem] = useState({});
	const [hiddenUnsafeTasks, setHiddenUnsafeTasks] = useState([]);
	const [orderDashboardData, setOrderDashboardData] = useState([]);
	const [placeOrderDeleveryInspactionColumn, setPlaceOrderDeleveryInspactionColumn] = useState([]);
	const orderDashboardStore = useSelector((store) => store.orderDashboardStore);
	const filterFields = useSelector((store) => store.filterFieldStore);
	const location = useLocation();
	const dispatch = useDispatch();
	const elementRef = useRef(null);
	const [arrowDisable, setArrowDisable] = useState(true);
	const newMessages = useSelector((store) => store.chatReducer.newMessage);
	const [deliverableList, setDeliverableList] = useState([]);
	const [isTnaStatusModalHasSet, setIsTnaStatusModalHasSet] = useState(false);
	const [streamList, setStreamList] = useState([]);
	const [groupByCount, setGroupByCount] = useState([]);
	const [selectedGroupBy, setSelectedGroupBy] = useState(null);
	const [activePage, setActivePage] = useState(null);
	const [orderDashboardId, setOrderDashboardId] = useState(null);
	const [groupWiseParams, setGroupWiseParams] = useState('');
	const [selectedStepName, setSelectedStepName] = useState(null);
	const [selectedStepIdList, setSelectedStepIdList] = useState([]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const queryParam = params.get('active');
		if (queryParam) {
			setActivePage('ORDER_DASHBOARD');
		}
	}, [location.search]);

	useEffect(() => {
		setGroupWiseParams(queryParams);
	}, [queryParams]);

	const getGroupWiseCount = async (groupWiseParams) => {
		const sort = filterFields?.sort || [];
		let params = '';

		sort.forEach((item) => {
			if (params.length > 0) {
				params += '&';
			}
			if (item.value === 'Group by TNA status') {
				params += `groupBy=TNA_STATUS`;
			} else if (item.value === 'Group by order') {
				params += `groupBy=ORDER`;
			} else if (item.value === 'Group by brand') {
				params += `groupBy=BRAND`;
			}
		});
		params += groupWiseParams;

		const ordersList = await tnaService.getGroupWiseOrderCount(`?${params}`);
		setGroupByCount(Array.isArray(ordersList) ? ordersList : []);
	};

	useEffect(() => {
		const sort = filterFields?.sort || [];
		sort.forEach((item) => {
			if (item.value === 'Group by TNA status') {
				setSelectedGroupBy('TNA_STATUS');
			} else if (item.value === 'Group by order') {
				setSelectedGroupBy('ORDER');
			} else if (item.value === 'Group by brand') {
				setSelectedGroupBy('BRAND');
			} else {
				setSelectedGroupBy('Ungrouped');
			}
		});
	}, [filterFields]);

	useEffect(() => {
		(async () => {
			if (requestFrom === 'ORDER_DASHBOARD' && groupWiseParams)
				await getGroupWiseCount(groupWiseParams);
		})();
	}, [groupWiseParams, requestFrom]);

	useEffect(() => {
		(async () => {
			try {
				await fetchEnumValues('info.nitex.app.common.enums.OrderStream', setStreamList);
			} catch {
				/* Empty Block */
			}
		})();
	}, []);

	useEffect(() => {
		setCurrentActivePage({
			id: null,
			type: 'ORDER_DASHBOARD_UNREAD_POST_DELIVERABLE',
			pageType: 'DETAILS'
		});
	}, []);

	useEffect(() => {
		const orderDashboardList = orderDashboardStore?.headings.filter(
			(order) => !filterBy.includes(order.name)
		);
		const extractColumns = orderDashboardStore?.headings.filter((order) => filterBy.includes(order.name));
		setPlaceOrderDeleveryInspactionColumn(extractColumns);
		setOrderDashboardData(orderDashboardList);
	}, [orderDashboardStore.headings.length]);

	useEffect(() => {
		if (Array.isArray(newMessages)) {
			setDeliverableList(newMessages);
		}
	}, [newMessages]);

	const toggleTaskMessageRead = (id) => {
		let tmpList = deliverableList.filter((item) => parseInt(item) !== parseInt(id));
		setDeliverableList(tmpList);
		dispatch(storeNewMsg(tmpList));
	};

	useEffect(() => {
		let uniqueOrderIds = [...new Map(orderStore.map((item) => [item['orderId'], item])).values()];
		let orderObj = {};
		let colorNumber = 1;
		for (let i = 0; i <= uniqueOrderIds.length; i++) {
			if (colorNumber === 6) {
				colorNumber = 1;
			}
			if (uniqueOrderIds[i]?.orderId) {
				orderObj[uniqueOrderIds[i].orderId] = `color-${colorNumber}`;
			}
			colorNumber++;
		}
		setColors(orderObj);
	}, [orderStore]);

	const renderAllCurrentStepCheckbox = (item, isInspection = false) => {
		if (requestFrom === 'ORDER_DETAILS' && selectedStepName === item.name) {
			let stepListIds = [];
			const addStepId = (tnaResponse) => {
				let selectedStep = tnaResponse.find((step) => step.name === item.name);
				if (selectedStep && !['APPROVED', 'COMPLETED'].includes(selectedStep.currentStep.status)) {
					stepListIds.push(selectedStep.currentStep.id);
				}
			};
			orderStore.forEach((order) => {
				if (order.setPackStyleFlag === 'SET_STYLE' && !isInspection) {
					order.tnaSetMemberResponseList.forEach((tnaResponse) => addStepId(tnaResponse));
				} else {
					addStepId(order.tnaResponse);
				}
			});
			return (
				<div className={`custom-chekbox`}>
					<div className="form-group mb-0">
						<input
							type="checkbox"
							name={`${item.name}`}
							id={`${item.name}`}
							value={item.name}
							onChange={(e) => {
								e.stopPropagation();
								if (stepListIds.length !== selectedStepIdList.length) {
									setSelectedStepIdList(stepListIds);
								} else {
									setSelectedStepIdList([]);
									setSelectedStepName(null);
								}
							}}
							checked={selectedStepIdList.length === stepListIds.length}
						/>
						<label htmlFor={`${item.name}`} />
					</div>
				</div>
			);
		}
	};

	const renderTableHeading = () => {
		return orderDashboardData.map((item, index) => {
			if (
				orderDashboardStore.isAllColumnVisible ||
				(orderDashboardStore.isCriticalColumnVisible && item.isCritical) ||
				(orderDashboardStore.isSweaterColumnVisible && item.applicableFabric === 'SWEATER') ||
				(orderDashboardStore.isProductionColumnVisible && item?.stageConstants === 'PRODUCTION') ||
				(orderDashboardStore.isSampleColumnVisible && item?.stageConstants === 'SAMPLING') ||
				item.isColumnVisible
			) {
				return (
					<th key={`table_head_${index}`}>
						<div className="d-flex align-items-center align-self-center">
							{renderAllCurrentStepCheckbox(item)}
							<Tooltip title={item?.name} placement={'top'} arrow>
								<span className="">{getShortName(item?.name, 16)}</span>
							</Tooltip>
						</div>
					</th>
				);
			}
		});
	};

	const isExist = (masterId, idList) => {
		return idList.includes(masterId);
	};

	const handleCellClick = (taskInfo, style, rfqId) => {
		setOrderDashboardId(rfqId);
		toggleTaskMessageRead(taskInfo.id);
		setSelectedTaskId(taskInfo.id);
		setOrderId(style.orderId);
		setBrandName(style.brandName);
		setShowTaskDetailsModal(true);
	};

	const renderCheckbox = (stepInfo) => {
		if (selectedStepName === null || selectedStepName === stepInfo.name) {
			let { id, tnaStatus } = stepInfo?.currentStep;
			return (
				<div
					className={`custom-chekbox ${['UNSAFE', 'RED_ALERT', 'DUE_SOON'].includes(tnaStatus) ? 'light-checkbox' : ''}`}
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<div className="form-group mb-1">
						<input
							type="checkbox"
							id={`${stepInfo.name}_${id}`}
							name={`${stepInfo.name}_${id}`}
							value={id}
							checked={selectedStepIdList.includes(id)}
							onChange={() => {
								setSelectedStepName(stepInfo.name);
								if (selectedStepIdList.includes(id)) {
									let modifiedList = selectedStepIdList.filter((item) => item !== id);
									setSelectedStepIdList(modifiedList);
									if (modifiedList.length === 0) {
										setSelectedStepName(null);
									}
								} else {
									setSelectedStepIdList([...selectedStepIdList, id]);
								}
							}}
						/>
						<label htmlFor={`${stepInfo.name}_${id}`} />
					</div>
				</div>
			);
		}
	};

	const renderStepsTableDetails = (item, tnaStatus, style, rowSpan = 1, rfqId) => {
		let stepInfo =
			tnaStatus &&
			tnaStatus.find((task) => isExist(task.deliverableMasterId, item.deliverableMasterIdList));
		if (stepInfo === undefined) {
			return (
				<td className="regular-12 gray_dark_02" key={`steps_${_getKey()}`} rowSpan={rowSpan}>
					<p className="regular-12 gray_dark_01">N/A</p>
				</td>
			);
		} else {
			let dueDay = getDateDifference(new Date(), stepInfo?.currentStep?.endDate);
			return (
				<td
					className={`regular-12 cursor-pointer gray_dark_02 ${stepInfo?.currentStep?.tnaStatus
						?.toLowerCase()
						?.replace('_', '-')} ${selectedStepName === stepInfo.name ? 'active' : ''}`}
					key={`steps_${_getKey()}`}
					onClick={() => handleCellClick(stepInfo, style, rfqId)}
					rowSpan={rowSpan}
				>
					{!['APPROVED', 'COMPLETED'].includes(stepInfo?.currentStep?.status) &&
						stepInfo?.stageConstants !== 'DELIVERY' &&
						renderCheckbox(stepInfo)}
					{!stepInfo?.currentStep?.productionTarget ? (
						<div className="d-flex align-items-center">
							{['APPROVED', 'COMPLETED'].includes(stepInfo?.currentStep?.status) && (
								<span>
									<CommonImage
										src="/icons/tick24.svg"
										alt="down"
										className="mr-1"
										width={18}
									/>
								</span>
							)}
							<p className="regular-12 gray_dark_01">{stepInfo?.currentStep?.dashboardName}</p>
						</div>
					) : (
						<div className="production-qty">
							<span className="semibold-12">
								{stepInfo?.currentStep?.actualProductionQuantity || 0}
							</span>
							/{stepInfo?.currentStep?.productionTarget}
						</div>
					)}

					{(stepInfo?.currentStep?.formattedTaskStatus === 'LATE_APPROVED' ||
						stepInfo?.currentStep?.formattedTaskStatus === 'APPROVED' ||
						stepInfo?.currentStep?.formattedTaskStatus === 'COMPLETED') && (
						<p className="regular-12 green">
							{stepInfo?.currentStep?.actualEndDate
								? `C: ${changeDateFormat(
										stepInfo?.currentStep?.actualEndDate,
										'YYYY-MM-DD',
										'DD MMM'
									)}`
								: ''}
							&nbsp;
							{stepInfo?.currentStep?.formattedTaskStatus === 'LATE_APPROVED' ? (
								<span className="red">+{Math.abs(stepInfo?.currentStep?.dateOver)}</span>
							) : (
								`${Math.abs(stepInfo?.currentStep?.dateOver) > 0 ? '+' : ''}${Math.abs(
									stepInfo?.currentStep?.dateOver
								)}`
							)}
						</p>
					)}

					{stepInfo?.currentStep?.endDate && (
						<p className="regular-12 gray_dark_02">
							{stepInfo?.currentStep?.endDate
								? `P: ${changeDateFormat(
										stepInfo?.currentStep?.endDate,
										'YYYY-MM-DD',
										'DD MMM'
									)}`
								: ''}
						</p>
					)}

					{(stepInfo?.currentStep?.tnaStatus === 'DUE_SOON' ||
						stepInfo?.currentStep?.tnaStatus === 'RED_ALERT') && (
						<span className="status white">
							Due in&nbsp;
							<span>{Math.abs(dueDay)}D</span>
						</span>
					)}

					{stepInfo?.currentStep?.tnaStatus === 'UNSAFE' && (
						<span className="status white">
							<img src={`${process.env.PUBLIC_URL}/icons/Unsafe-14.svg`} alt="" />
							<span>+{Math.abs(dueDay)}d</span>
						</span>
					)}

					{deliverableList.includes(stepInfo?.id) && (
						<span className="comment-icon">
							<CommentIcon />
						</span>
					)}
				</td>
			);
		}
	};

	const checkActiveColumn = (item) => {
		return (
			orderDashboardStore.isAllColumnVisible ||
			(orderDashboardStore.isCriticalColumnVisible && item.isCritical) ||
			(orderDashboardStore.isSweaterColumnVisible && item.applicableFabric === 'SWEATER') ||
			(orderDashboardStore.isProductionColumnVisible && item?.stageConstants === 'PRODUCTION') ||
			(orderDashboardStore.isSampleColumnVisible && item?.stageConstants === 'SAMPLING') ||
			item.isColumnVisible
		);
	};

	const renderSteps = (tnaStatus, style, rfqId) => {
		return orderDashboardData.map((item) => {
			if (checkActiveColumn(item)) {
				return renderStepsTableDetails(item, tnaStatus, style, 1, rfqId);
			}
		});
	};

	const renderEmptySteps = () => {
		return orderDashboardData.map((item, index, isSet) => {
			if (checkActiveColumn(item)) {
				return <td key={`steps-${index}-${isSet}`}>N/A</td>;
			}
		});
	};

	const handleShowAllTask = (item) => {
		setSelectedItem(item);
		setIsTnaStatusModalHasSet(item?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT);
		setShowAllTask(true);
	};

	const closeAllTaskModal = () => {
		setHiddenUnsafeTasks([]);
		setSelectedItem({});
		setShowAllTask(false);
	};

	const isTnaResponseHasLength = (tnaResponseArray = []) => {
		return tnaResponseArray?.length > 0;
	};

	const showTaskCounts = (item) => {
		if (!item.tnaStatusCountMap) return;
		if (!Object.keys(item.tnaStatusCountMap).length > 0) return;
		let countMap = item.tnaStatusCountMap;
		let tmpArray = [];
		for (let type in countMap) {
			if (type === 'SAFE' || type === 'SHIPPED') continue;
			let obj = {
				type: type.replace('_', '-').toLowerCase(),
				content: `${type.replace('_', ' ')}`,
				count: countMap[type]
			};
			if (type === item.tnaStatus) {
				tmpArray.unshift(obj);
			} else {
				tmpArray.push(obj);
			}
		}
		return tmpArray?.map((item) => {
			return (
				<span className={`status mt-2 cursor-pointer ${item.type}`} key={item.type}>
					{item?.content}&nbsp;
					<span className={'count'}>{item?.count}</span>
				</span>
			);
		});
	};

	const renderTableBody = (items) => {
		const groupedDataMap = new Map();
		const orderTracker = [];

		items.forEach((item) => {
			const { productId, setMemberIds = [], ...rest } = item;

			if (!groupedDataMap.has(productId)) {
				groupedDataMap.set(productId, {
					productId,
					...rest,
					setMemberIds: [...new Set(setMemberIds)],
					etd: []
				});

				orderTracker.push(productId);
			}

			groupedDataMap.get(productId).etd.push({
				productId,
				...rest,
				setMemberIds: [...new Set(setMemberIds)]
			});
		});

		const result = orderTracker.map((productId) => groupedDataMap.get(productId));

		const renderBody = (key, item, isProductOfSet, idx, rowSpanCount, orderInfoMap, tnaResponse) => {
			let selectedOrderInfo = orderInfoMap?.[`${item.id}_${idx}`];
			let isKeyExist = `${item.id}_${idx}` in orderInfoMap;
			let rfqId = orderInfoMap[`${item.id}_${idx}`]?.id;
			return (
				<tr key={key}>
					{idx === 0 &&
						orderDashboardStore?.isBrandColumnVisible &&
						requestFrom !== 'ORDER_DETAILS' && (
							<BrandAndTeamCell rowSpan={rowSpanCount} item={item} />
						)}
					{idx === 0 && orderDashboardStore?.isStyleColumnVisible && (
						<StyleCell
							isProductOfSet={isProductOfSet}
							rowSpan={rowSpanCount}
							item={item}
							openDesignViewModal={openDesignViewModal}
						/>
					)}
					{idx === 0 &&
						orderDashboardStore?.isFabricColumnVisible &&
						requestFrom !== 'ORDER_DETAILS' && <FabricCell rowSpan={rowSpanCount} item={item} />}
					{orderDashboardStore?.isETDColumnVisible && isKeyExist && (
						<BuyerEtdCell
							index={selectedOrderInfo?.idx}
							date={selectedOrderInfo?.buyerEtd}
							rowSpan={selectedOrderInfo?.rowSpanCount}
						/>
					)}
					{orderDashboardStore?.isETDColumnVisible && isKeyExist && (
						<FactoryEtdCell
							date={selectedOrderInfo?.factoryEtd}
							rowSpan={selectedOrderInfo?.rowSpanCount}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isPOColumnVisible && (
						<PoCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
							streamList={streamList}
						/>
					)}

					{isKeyExist && orderDashboardStore?.isPriceColumnVisible && (
						<QuotationBuyerPriceCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isPriceColumnVisible && (
						<QuotationFactoryPriceCell
							rowSpan={selectedOrderInfo?.rowSpanCount}
							item={selectedOrderInfo}
						/>
					)}
					{isKeyExist && orderDashboardStore?.isSCColumnVisible && (
						<ScCell rowSpan={selectedOrderInfo?.rowSpanCount} item={item} />
					)}
					{orderDashboardStore?.isTnaStatusColumnVisible && isKeyExist && (
						<TnaStatusCell
							item={selectedOrderInfo}
							onClick={() => {
								handleShowAllTask(item);
							}}
							showTaskCounts={showTaskCounts}
							rowSpan={selectedOrderInfo?.rowSpanCount}
						/>
					)}

					{isShowTasks && (
						<>
							{isTnaResponseHasLength(isProductOfSet ? tnaResponse : tnaResponse[0])
								? renderSteps(isProductOfSet ? tnaResponse : tnaResponse[0], item, rfqId)
								: renderEmptySteps(item, isProductOfSet)}
						</>
					)}

					{isKeyExist &&
						placeOrderDeleveryInspactionColumn.map((lastTwoHeading) => {
							const isDelivery =
								lastTwoHeading.stageConstants === 'DELIVERY' &&
								orderDashboardStore.isDeliveryColumnVisible;
							const isInspection =
								lastTwoHeading.stageConstants === 'INSPECTION' &&
								orderDashboardStore.isInspectionColumnVisible;

							if (isDelivery || isInspection) {
								return renderStepsTableDetails(
									lastTwoHeading,
									selectedOrderInfo?.tnaResponse,
									item,
									selectedOrderInfo?.rowSpanCount,
									rfqId
								);
							}
						})}

					{isKeyExist && orderDashboardStore?.isExFactoryColumnVisible && (
						<ExFactoryCell rowSpan={selectedOrderInfo?.rowSpanCount} item={selectedOrderInfo} />
					)}
					{isKeyExist && orderDashboardStore?.isSPColumnVisible && (
						<ShipmentCell rowSpan={selectedOrderInfo?.rowSpanCount} item={selectedOrderInfo} />
					)}
					{isKeyExist && isShowRemarks && (
						<td rowSpan={selectedOrderInfo?.rowSpanCount}>
							<OrderRemarks id={selectedOrderInfo.id} message={selectedOrderInfo.remarks} />
						</td>
					)}
				</tr>
			);
		};

		return result?.map((order) => {
			const isProductOfSet = order?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT;
			let tnaResponseList = [];
			let orderInfoMap = {};
			let etdInterVal = 0;

			order?.etd.forEach((item, idx) => {
				const setMemberResponseCount = Array.isArray(item.tnaSetMemberResponseList)
					? item.tnaSetMemberResponseList.length
					: 0;
				const rowSpanCountForOrderInfo = isProductOfSet ? Math.max(setMemberResponseCount, 1) : 1;

				if (isProductOfSet) {
					if (setMemberResponseCount > 0) {
						tnaResponseList.push(...item.tnaSetMemberResponseList);
					} else {
						// Fallback to render a single row when set member responses are missing.
						tnaResponseList.push(item.tnaResponse || []);
					}
				} else {
					tnaResponseList.push([item.tnaResponse]);
				}

				orderInfoMap[`${order.id}_${etdInterVal}`] = {
					id: item.id,
					remarks: item.remarks,
					commercialInvoiceAmount: item.commercialInvoiceAmount,
					commercialInvoiceDate: item.commercialInvoiceDate,
					commercialInvoiceNo: item.commercialInvoiceNo,
					shippedQuantity: item.shippedQuantity,
					factoryName: item.factoryName,
					salesContractRefNo: item.salesContractRefNo,
					salesContractAmount: item.salesContractAmount,
					amendmentNo: item.amendmentNo,
					orderId: item.orderId,
					orderName: item.orderName,
					orderRefNo: item.orderRefNo,
					poQuantity: item.poQuantity,
					poValue: item.poValue,
					poReceivedDate: item.poReceivedDate,
					margin: item.margin,
					marginPercent: item.marginPercent,
					stream: item.stream,
					buyerEtd: item.buyerEtd,
					factoryEtd: item.factoryEtd,
					tnaStatus: item.tnaStatus,
					tnaStatusCountMap: item.tnaStatusCountMap,
					tnaResponse: item.tnaResponse,
					exFactoryDate: item.exFactoryDate,
					exFactoryQuantity: item.exFactoryQuantity,
					exFactoryAmount: item.exFactoryAmount,
					skuList: item.skuList,
					buyerQuotationType: item.buyerQuotationType,
					factoryQuotationType: item.factoryQuotationType,
					designWiseBuyerPrice: item.designWiseBuyerPrice,
					designWiseFactoryPrice: item.designWiseFactoryPrice,
					sizeWiseBuyerPriceResponse: item.sizeWiseBuyerPriceResponse,
					sizeWiseFactoryPriceResponse: item.sizeWiseFactoryPriceResponse,
					colorWiseBuyerPriceResponse: item.colorWiseBuyerPriceResponse,
					colorWiseFactoryPriceResponse: item.colorWiseFactoryPriceResponse,
					idx,
					rowSpanCount: rowSpanCountForOrderInfo
				};

				etdInterVal += rowSpanCountForOrderInfo;
			});

			return tnaResponseList?.map((tnaResponse, idx) =>
				renderBody(
					`order_dashboard_${order.id}_${idx}`,
					order,
					isProductOfSet,
					idx,
					tnaResponseList.length,
					orderInfoMap,
					tnaResponse
				)
			);
		});
	};

	const closeModal = () => {
		setOrderDashboardId(null);
		setReviseInfo(null);
		setOrderId(null);
		setBrandName('');
		setShowTaskDetailsModal(false);
	};

	const resetRevisionInfo = () => {
		setReviseInfo(null);
	};

	const handleHorizontalScroll = (element, speed, distance, step) => {
		let scrollAmount = 0;
		const slideTimer = setInterval(() => {
			element.scrollLeft += step;
			scrollAmount += Math.abs(step);
			if (scrollAmount >= distance) {
				clearInterval(slideTimer);
			}
			if (element.scrollLeft === 0) {
				setArrowDisable(true);
			} else {
				setArrowDisable(false);
			}
		}, speed);
	};

	const openDesignViewModal = (design) => {
		dispatch(
			setDesignViewModalInfo({
				isShowMergeTechPack:
					design?.isSet || design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT,
				id: design?.productId,
				onCloseViewModal: () => {
					dispatch(clearDesignViewModalInfo({}));
				},
				source: 'ORDER_DETAILS',
				orderId: design?.orderId
			})
		);
	};

	const groupWiseOrderList = (data, groupByCount) => {
		if (
			selectedGroupBy === ['TNA_STATUS', 'BRAND', 'ORDER'] &&
			(!Array.isArray(data) || data.length === 0)
		) {
			return [];
		}

		if (
			selectedGroupBy === ['TNA_STATUS', 'BRAND', 'ORDER'] &&
			(!Array.isArray(groupByCount) || groupByCount.length === 0)
		) {
			return [];
		}

		const groupWiseMap = groupByCount.reduce((acc, item) => {
			if (selectedGroupBy === 'TNA_STATUS' || selectedGroupBy === 'BRAND') {
				acc[item.name] = item.name;
			} else {
				acc[item.id] = item.name;
			}

			return acc;
		}, {});

		const groupedList = data.reduce((acc, item) => {
			let key;
			if (selectedGroupBy === 'TNA_STATUS') {
				key = groupWiseMap[item.tnaStatus];
			} else if (selectedGroupBy === 'BRAND') {
				key = groupWiseMap[item.brandName];
			} else if (selectedGroupBy === 'ORDER') {
				key = groupWiseMap[item.orderId];
			} else {
				key = 'Ungrouped';
			}
			if (key) {
				(acc[key] = acc[key] || []).push(item);
			}
			return acc;
		}, {});

		return Object.entries(groupedList);
	};

	const calculateExpectedMargin = (findGroupByCount) => {
		return parseFloat(findGroupByCount?.amount) - parseFloat(findGroupByCount?.productionCost);
	};

	const renderCommonTableHeadings = (showGroupWiseTitle = true, findGroupByCount, groupKey, items) => {
		let groupHeading = document.getElementById('table-hading-group');
		let margin = calculateExpectedMargin(findGroupByCount).toFixed(2);
		return (
			<thead>
				{showGroupWiseTitle && (
					<tr className="summary-row">
						{groupHeading?.childElementCount && (
							<td colSpan={groupHeading?.childElementCount} className="bg-gray">
								<h3 className="semibold-16">
									{capitalizeFirstLetter(groupKey).split('_').join(' ')}
									{findGroupByCount?.styleCount &&
										` (${findGroupByCount?.styleCount} Styles / $${formatNumberWithSuffix(
											findGroupByCount?.amount
										)} ${!isNaN(margin) ? `- Expected Margin $${formatNumberWithSuffix(margin)}` : ''})`}
								</h3>
							</td>
						)}
					</tr>
				)}
				<tr id="table-hading-group" className="heading-row">
					{orderDashboardStore?.isBrandColumnVisible && requestFrom !== 'ORDER_DETAILS' && (
						<th className="brand-and-team">Brand &amp; Team</th>
					)}
					{orderDashboardStore?.isStyleColumnVisible && <th className="style-column">Style</th>}
					{orderDashboardStore?.isFabricColumnVisible && requestFrom !== 'ORDER_DETAILS' && (
						<th>Fabric</th>
					)}
					{orderDashboardStore?.isETDColumnVisible && (
						<th className="etd-column buyer-etd">Buyer ETD</th>
					)}
					{orderDashboardStore?.isETDColumnVisible && (
						<th className="etd-column factory-etd">Factory ETD</th>
					)}
					{orderDashboardStore?.isPOColumnVisible && <th>PO details</th>}
					{orderDashboardStore?.isPriceColumnVisible && <th>Buyer Price</th>}
					{orderDashboardStore?.isPriceColumnVisible && <th>Factory Price</th>}
					{orderDashboardStore?.isSCColumnVisible && <th className="sc-details">SC details</th>}
					{orderDashboardStore?.isTnaStatusColumnVisible && (
						<th className="tna-status">TNA status</th>
					)}
					{isShowTasks && renderTableHeading(items)}
					{placeOrderDeleveryInspactionColumn.map((lastTwoHeading, index) => {
						const isDelivery =
							lastTwoHeading.stageConstants === 'DELIVERY' &&
							orderDashboardStore.isDeliveryColumnVisible;
						const isInspection =
							lastTwoHeading.stageConstants === 'INSPECTION' &&
							orderDashboardStore.isInspectionColumnVisible;
						if (isDelivery || isInspection) {
							return (
								<th key={`${lastTwoHeading?.name}-${index}`}>
									<div className="d-flex align-items-center align-self-center">
										{isInspection && renderAllCurrentStepCheckbox(lastTwoHeading, true)}
										{lastTwoHeading?.name}
									</div>
								</th>
							);
						}
					})}
					{orderDashboardStore?.isExFactoryColumnVisible && <th>Ex-factory Details</th>}
					{orderDashboardStore?.isSPColumnVisible && <th>Shipment Details</th>}
					{isShowRemarks && <th>Remarks</th>}
				</tr>
			</thead>
		);
	};

	const renderGroupWiseTable = () => {
		const groupedOrders = groupWiseOrderList(orderStore, groupByCount);

		return (
			<>
				{groupedOrders.map(([groupKey, items], index) => {
					const findGroupByCount = groupByCount.find((group) => group?.name == groupKey);
					return (
						<React.Fragment key={`${groupKey}-${index}`}>
							{renderCommonTableHeadings(
								groupKey !== 'Ungrouped',
								findGroupByCount,
								groupKey,
								items
							)}
							<tbody>{renderTableBody(items)}</tbody>
						</React.Fragment>
					);
				})}
			</>
		);
	};

	const renderTable = () => {
		return (
			<React.Fragment>
				{renderCommonTableHeadings(false)}
				<tbody>{renderTableBody(orderStore)}</tbody>
			</React.Fragment>
		);
	};

	const handleTaskComplete = async () => {
		try {
			dispatch(openLoader());
			const data = await completeOrderStepByIdList(selectedStepIdList);
			updateOrderStepByOrders(data);
			setSelectedStepIdList([]);
			setSelectedStepName(null);
			dispatch(closeLoader());
		} catch (e) {
			printApiError(e);
			dispatch(closeLoader());
		}
	};

	return (
		<LoaderComponent loading={loader}>
			{showSummary && <OrderSummary />}
			<div className="scroll-table-buttons">
				<button
					className="left"
					onClick={() => {
						handleHorizontalScroll(elementRef.current, 25, 100, -400);
					}}
					disabled={arrowDisable}
				>
					<SlideIcon />
				</button>
				&nbsp;&nbsp;
				<button
					className="right"
					onClick={() => {
						handleHorizontalScroll(elementRef.current, 25, 100, 400);
					}}
				>
					<SlideIcon />
				</button>
			</div>
			<div className="order-dashboard">
				<table
					id="orderDashboardTable"
					className={
						showSummary
							? `table with-border order-dashboard-table scroll-x-label scroll-y-label`
							: `table with-border order-dashboard-table hide-summary scroll-x-label scroll-y-label`
					}
					ref={elementRef}
					onScroll={handleOrderDashboardScroll}
				>
					{activePage === 'ORDER_DASHBOARD' ? renderGroupWiseTable() : renderTable()}
				</table>
			</div>
			<Modal
				show={showTaskDetailsModal}
				onHide={() => {
					setShowTaskDetailsModal(false);
					closeModal();
				}}
				className="modal-right task-conversation with-tag-person"
				aria-labelledby="example-custom-modal-styling-title"
			>
				<OrderContext.Provider
					value={{
						deliverableId: selectedTaskId,
						brandName,
						reviseInfo,
						orderId,
						closeModal,
						resetRevisionInfo
					}}
				>
					<OrderTask
						rfqId={orderDashboardId}
						requestFrom="ORDER_DASHBOARD"
						callback={async () => {
							await getOrderInfoByDashboardId(orderDashboardId);
						}}
					/>
				</OrderContext.Provider>
			</Modal>
			<Modal
				show={showAllTask}
				onHide={closeAllTaskModal}
				className="modal-right task-conversation with-tag-person"
				aria-labelledby="example-custom-modal-styling-title"
			>
				<AllTask
					closeModal={closeAllTaskModal}
					hiddenTasks={hiddenUnsafeTasks}
					styleInfo={selectedItem}
					callback={() => callback()}
					isSetProduct={isTnaStatusModalHasSet}
				/>
			</Modal>
			{selectedStepIdList.length > 0 && (
				<div className="design-count-section bg-white order-task-popup d-flex justify-content-between align-items-center">
					<p className="regular-14 weight-500 cursor-pointer" onClick={handleTaskComplete}>
						Complete {selectedStepIdList.length} task
					</p>
					<CommonImage
						src="/icons/close.svg"
						alt="down"
						className="ml-1 close-icon cursor-pointer"
						onClick={() => {
							setSelectedStepIdList([]);
							setSelectedStepName(null);
						}}
					/>
				</div>
			)}
		</LoaderComponent>
	);
};

export default OrderDashboard;
